import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Send, Plus, Edit, Trash2, Eye, Bell, Users, Settings, BarChart3, Smartphone, Power, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatNyDateTime } from '../../utils/datetime';

const PushNotificationsPanel = ({ language = 'pt-BR' }) => {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  
  // Estados
  const [templates, setTemplates] = useState({});
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [devices, setDevices] = useState({ devices_by_user: {}, total_devices: 0, active_devices: 0 });
  const [loading, setLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  
  // Modal de envio manual
  const [showSendModal, setShowSendModal] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [sendForm, setSendForm] = useState({
    recipients: [],
    template_key: 'custom',
    title: '',
    body: '',
    image_url: ''
  });
  
  // Modal de regras
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    trigger: 'new_job',
    template_key: 'new_job',
    recipients: [],
    active: true,
    custom_title: '',
    custom_body: '',
    schedule_time: '',
    schedule_days_before: 1
  });
  
  // Modal de templates
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    key: '',
    title: '',
    body: '',
    data: { type: '' }
  });
  
  // Carregar dados
  useEffect(() => {
    loadTemplates();
    loadRules();
    loadLogs();
    loadDevices();
  }, []);
  
  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.get(`${API}/push/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setDevices(data);
      }
    } catch (error) {
      console.error('Erro ao carregar dispositivos:', error);
    } finally {
      setDevicesLoading(false);
    }
  };
  
  const handleToggleDevice = async (deviceId) => {
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.put(`${API}/push/devices/${deviceId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        toast.success(data.message);
        loadDevices();
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao atualizar dispositivo' : 'Error updating device');
    }
  };
  
  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm(language === 'pt-BR' ? 'Deseja remover este dispositivo?' : 'Remove this device?')) {
      return;
    }
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.delete(`${API}/push/devices/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        toast.success(data.message);
        loadDevices();
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao remover dispositivo' : 'Error removing device');
    }
  };
  
  const loadTemplates = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.get(`${API}/push/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(data.templates || {});
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };
  
  const loadRules = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.get(`${API}/push/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(data.rules || []);
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    }
  };
  
  const loadLogs = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      const { data } = await axios.get(`${API}/push/logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    }
  };
  
  const handleResendPush = async (log) => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('access_token');
      const payload = {
        recipients: [log.user_id],
        template_key: 'custom',
        template_data: { title: log.title, body: log.body },
        send_immediately: true
      };
      const { data } = await axios.post(`${API}/push/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        toast.success(language === 'pt-BR' ? 'Push reenviado!' : 'Push resent!');
        loadLogs();
      }
    } catch (e) {
      toast.error(language === 'pt-BR' ? 'Erro ao reenviar' : 'Resend failed');
    } finally { setLoading(false); }
  };

  const handlePushImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'pt-BR' ? 'Selecione uma imagem (JPG/PNG)' : 'Select an image (JPG/PNG)');
      return;
    }
    setImageUploading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/push/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setSendForm(prev => ({ ...prev, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.image_path}` }));
        toast.success(language === 'pt-BR' ? '🖼️ Imagem anexada!' : '🖼️ Image attached!');
      }
    } catch (err) {
      toast.error(language === 'pt-BR' ? 'Falha ao enviar imagem' : 'Image upload failed');
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  const handleSendNotification = async () => {
    if (sendForm.template_key === 'custom' && (!sendForm.title || !sendForm.body)) {
      toast.error(language === 'pt-BR' ? 'Preencha título e mensagem' : 'Fill title and message');
      return;
    }
    
    if (sendForm.recipients.length === 0) {
      toast.error(language === 'pt-BR' ? 'Selecione ao menos um destinatário' : 'Select at least one recipient');
      return;
    }
    
    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      
      const payload = {
        recipients: sendForm.recipients,
        template_key: sendForm.template_key,
        template_data: sendForm.template_key === 'custom' 
          ? { title: sendForm.title, body: sendForm.body }
          : { title: sendForm.title, body: sendForm.body },
        send_immediately: true,
        image_url: sendForm.image_url || null
      };
      
      console.log('📤 Sending notification...', payload);
      
      const { data } = await axios.post(`${API}/push/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Send response:', data);
      
      if (data.success) {
        toast.success(
          language === 'pt-BR' 
            ? `✅ ${data.sent_count} notificação(ões) enviada(s)!` 
            : `✅ ${data.sent_count} notification(s) sent!`
        );
        setShowSendModal(false);
        setSendForm({ recipients: [], template_key: 'custom', title: '', body: '', image_url: '' });
        
        // Try to load logs, but don't fail if it errors
        try {
          await loadLogs();
        } catch (logError) {
          console.warn('⚠️ Failed to load logs after send:', logError);
          // Don't show error to user - notification was sent successfully
        }
      } else {
        toast.error(data.message || 'Erro ao enviar');
      }
    } catch (error) {
      console.error('❌ Error sending notification:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(
        language === 'pt-BR' 
          ? `Erro ao enviar notificação: ${error.response?.data?.detail || error.message}` 
          : `Error sending notification: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };
  
  // Criar/editar regra
  const handleSaveRule = async () => {
    if (!ruleForm.name || ruleForm.recipients.length === 0) {
      toast.error(language === 'pt-BR' ? 'Preencha nome e destinatários' : 'Fill name and recipients');
      return;
    }
    
    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      
      if (editingRule) {
        // Editar
        await axios.put(`${API}/push/rules/${editingRule.id}`, ruleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'pt-BR' ? '✅ Regra atualizada!' : '✅ Rule updated!');
      } else {
        // Criar
        await axios.post(`${API}/push/rules`, ruleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'pt-BR' ? '✅ Regra criada!' : '✅ Rule created!');
      }
      
      setShowRuleModal(false);
      setEditingRule(null);
      setRuleForm({ name: '', trigger: 'new_job', template_key: 'new_job', recipients: [], active: true });
      loadRules();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao salvar regra' : 'Error saving rule');
    } finally {
      setLoading(false);
    }
  };
  
  // Deletar regra
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm(language === 'pt-BR' ? 'Deseja deletar esta regra?' : 'Delete this rule?')) {
      return;
    }
    
    try {
      const token = sessionStorage.getItem('access_token');
      await axios.delete(`${API}/push/rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'pt-BR' ? '✅ Regra deletada!' : '✅ Rule deleted!');
      loadRules();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao deletar' : 'Error deleting');
    }
  };
  
  // Toggle regra ativa/inativa
  const handleToggleRule = async (rule) => {
    try {
      const token = sessionStorage.getItem('access_token');
      await axios.put(`${API}/push/rules/${rule.id}`, {
        active: !rule.active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(
        language === 'pt-BR' 
          ? (rule.active ? '⏸️ Regra desativada' : '▶️ Regra ativada')
          : (rule.active ? '⏸️ Rule disabled' : '▶️ Rule enabled')
      );
      loadRules();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao atualizar' : 'Error updating');
    }
  };
  
  // Opções de destinatários
  const recipientOptions = [
    { value: 'all', label: language === 'pt-BR' ? '👥 Todos os Usuários' : '👥 All Users' },
    { value: 'all_admins', label: language === 'pt-BR' ? '👑 Todos os Admins' : '👑 All Admins' },
    { value: 'all_pms', label: language === 'pt-BR' ? '👷 Todos os PMs' : '👷 All PMs' },
    { value: 'PM1', label: '👤 PM1' },
    { value: 'PM2', label: '👤 PM2' },
    { value: 'PM3', label: '👤 PM3' },
    { value: 'PM4', label: '👤 PM4' },
    { value: 'PM5', label: '👤 PM5' },
    { value: 'PM6', label: '👤 PM6' },
    { value: 'PM7', label: '👤 PM7' },
    { value: 'PM8', label: '👤 PM8' },
    { value: 'PM9', label: '👤 PM9' },
    { value: 'PM10', label: '👤 PM10' }
  ];
  
  // Opções de triggers
  const triggerOptions = [
    { value: 'new_job', label: language === 'pt-BR' ? '🆕 Novo Trabalho Criado' : '🆕 New Job Created' },
    { value: 'job_updated', label: language === 'pt-BR' ? '✏️ Trabalho Atualizado' : '✏️ Job Updated' },
    { value: 'job_reminder', label: language === 'pt-BR' ? '⏰ Lembrete de Trabalho' : '⏰ Job Reminder' },
    { value: 'labor_bill_created', label: language === 'pt-BR' ? '💼 Labor Bill Criado' : '💼 Labor Bill Created' },
    { value: 'labor_bill_paid', label: language === 'pt-BR' ? '💰 Labor Bill Pago' : '💰 Labor Bill Paid' },
    { value: 'report_saved', label: language === 'pt-BR' ? '📊 Relatório Salvo' : '📊 Report Saved' },
    { value: 'ticket_created', label: language === 'pt-BR' ? '🎫 Ticket Criado' : '🎫 Ticket Created' },
    { value: 'ticket_replied', label: language === 'pt-BR' ? '💬 Ticket Respondido' : '💬 Ticket Replied' },
    { value: 'announcement_published', label: language === 'pt-BR' ? '📢 Anúncio Publicado' : '📢 Announcement Published' },
    { value: 'truck_rental_requested', label: language === 'pt-BR' ? '🚚 Aluguel Solicitado' : '🚚 Rental Requested' },
    { value: 'truck_rental_approved', label: language === 'pt-BR' ? '✅ Aluguel Aprovado' : '✅ Rental Approved' },
    { value: 'internal_notification_sent', label: language === 'pt-BR' ? '📨 Notificação Interna Enviada' : '📨 Internal Notification Sent' }
  ];
  
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            🔔 {language === 'pt-BR' ? 'Notificações Push' : 'Push Notifications'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'pt-BR' 
              ? 'Gerencie notificações push do aplicativo móvel' 
              : 'Manage mobile app push notifications'}
          </p>
        </div>
        
        <Button
          onClick={() => setShowSendModal(true)}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <Send className="h-4 w-4 mr-2" />
          {language === 'pt-BR' ? 'Enviar Notificação' : 'Send Notification'}
        </Button>
      </div>
      
      <Tabs defaultValue="devices" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="devices">
            <Smartphone className="h-4 w-4 mr-2" />
            {language === 'pt-BR' ? 'Dispositivos' : 'Devices'}
          </TabsTrigger>
          <TabsTrigger value="templates">
            <Bell className="h-4 w-4 mr-2" />
            {language === 'pt-BR' ? 'Templates' : 'Templates'}
          </TabsTrigger>
          <TabsTrigger value="rules">
            <Settings className="h-4 w-4 mr-2" />
            {language === 'pt-BR' ? 'Regras' : 'Rules'}
          </TabsTrigger>
          <TabsTrigger value="history">
            <BarChart3 className="h-4 w-4 mr-2" />
            {language === 'pt-BR' ? 'Histórico' : 'History'}
          </TabsTrigger>
        </TabsList>
        
        {/* ABA: Enviar Manual */}
        
        {/* ABA: Dispositivos Registrados */}
        <TabsContent value="devices" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">
                {language === 'pt-BR' 
                  ? 'Gerencie todos os celulares/dispositivos registrados para receber notificações' 
                  : 'Manage all registered devices for receiving notifications'}
              </p>
              <div className="flex gap-4 mt-2">
                <Badge className="bg-blue-100 text-blue-800">
                  📱 {devices.total_devices} {language === 'pt-BR' ? 'Total' : 'Total'}
                </Badge>
                <Badge className="bg-green-100 text-green-800">
                  ✅ {devices.active_devices} {language === 'pt-BR' ? 'Ativos' : 'Active'}
                </Badge>
                <Badge className="bg-gray-100 text-gray-800">
                  👥 {devices.total_users || Object.keys(devices.devices_by_user).length} {language === 'pt-BR' ? 'Usuários' : 'Users'}
                </Badge>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={loadDevices}
              disabled={devicesLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${devicesLoading ? 'animate-spin' : ''}`} />
              {language === 'pt-BR' ? 'Atualizar' : 'Refresh'}
            </Button>
          </div>
          
          {Object.keys(devices.devices_by_user).length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center text-gray-500">
                <Smartphone className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{language === 'pt-BR' ? 'Nenhum dispositivo registrado' : 'No devices registered'}</p>
                <p className="text-xs mt-1">
                  {language === 'pt-BR' 
                    ? 'Os dispositivos aparecerão aqui quando os usuários fizerem login no app' 
                    : 'Devices will appear here when users login on the app'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(devices.devices_by_user).map(([userId, userDevices]) => (
                <Card key={userId}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="text-xl">👤</span>
                      {userId}
                      <Badge className="ml-2">{userDevices.length} {language === 'pt-BR' ? 'dispositivo(s)' : 'device(s)'}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {userDevices.map((device, idx) => (
                        <div
                          key={device.id || idx}
                          className={`border rounded-lg p-3 flex items-center justify-between ${
                            device.active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {device.platform === 'ios' ? '📱' : device.platform === 'android' ? '🤖' : '💻'}
                              </span>
                              <span className="font-medium text-sm">
                                {device.platform?.toUpperCase() || 'Unknown'}
                              </span>
                              <Badge variant={device.active ? 'default' : 'secondary'} className="text-xs">
                                {device.active 
                                  ? (language === 'pt-BR' ? 'Ativo' : 'Active') 
                                  : (language === 'pt-BR' ? 'Inativo' : 'Inactive')}
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 font-mono">
                              Token: {device.token}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {device.created_at && (
                                <span>📅 {language === 'pt-BR' ? 'Registrado:' : 'Registered:'} {device.created_at}</span>
                              )}
                              {device.last_used && (
                                <span className="ml-3">🕐 {language === 'pt-BR' ? 'Último uso:' : 'Last used:'} {device.last_used}</span>
                              )}
                            </div>
                            {device.device_info && Object.keys(device.device_info).length > 0 && (
                              <div className="text-xs text-gray-400 mt-1">
                                ℹ️ {JSON.stringify(device.device_info)}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleDevice(device.id)}
                              title={device.active 
                                ? (language === 'pt-BR' ? 'Desativar' : 'Disable') 
                                : (language === 'pt-BR' ? 'Ativar' : 'Enable')}
                            >
                              <Power className={`h-4 w-4 ${device.active ? 'text-green-600' : 'text-gray-400'}`} />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteDevice(device.id)}
                              className="text-red-600 hover:text-red-700"
                              title={language === 'pt-BR' ? 'Remover' : 'Remove'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        

        {/* ABA: Templates */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {language === 'pt-BR' 
                ? 'Gerencie templates de notificações. Edite os existentes ou crie novos.' 
                : 'Manage notification templates. Edit existing ones or create new.'}
            </p>
            <Button
              onClick={() => {
                setEditingTemplate(null);
                setTemplateForm({ key: '', title: '', body: '', data: { type: '' } });
                setShowTemplateModal(true);
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              {language === 'pt-BR' ? 'Novo Template' : 'New Template'}
            </Button>
          </div>
          
          {Object.keys(templates).length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{language === 'pt-BR' ? 'Nenhum template configurado' : 'No templates configured'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(templates).map(([key, template]) => (
                <Card key={key} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{template.title}</h3>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">{key}</code>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTemplate(key);
                            setTemplateForm({
                              key: key,
                              title: template.title,
                              body: template.body,
                              data: template.data || { type: key }
                            });
                            setShowTemplateModal(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {key !== 'custom' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (window.confirm(language === 'pt-BR' ? 'Deletar este template?' : 'Delete this template?')) {
                                try {
                                  const token = sessionStorage.getItem('access_token');
                                  await axios.delete(`${API}/push/templates/${key}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success(language === 'pt-BR' ? 'Template deletado!' : 'Template deleted!');
                                  loadTemplates();
                                } catch (error) {
                                  console.error('Erro:', error);
                                  toast.error(language === 'pt-BR' ? 'Erro ao deletar' : 'Error deleting');
                                }
                              }
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="bg-gray-50 p-2 rounded">
                        <span className="font-medium text-gray-700">{language === 'pt-BR' ? 'Mensagem:' : 'Message:'}</span>
                        <p className="text-gray-600 mt-1">{template.body}</p>
                      </div>
                      
                      {template.data && Object.keys(template.data).length > 0 && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">Data:</span> {JSON.stringify(template.data)}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ABA: Regras Automáticas */}
        <TabsContent value="rules" className="space-y-6">
          
          {/* SEÇÃO 1: Triggers Disponíveis */}
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                🎯 {language === 'pt-BR' ? 'Eventos Disponíveis' : 'Available Events'}
              </h3>
              <p className="text-sm text-gray-600">
                {language === 'pt-BR' 
                  ? 'Escolha quais eventos devem disparar notificações automáticas' 
                  : 'Choose which events should trigger automatic notifications'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {triggerOptions.map((trigger) => {
                const activeRulesCount = rules.filter(r => r.trigger === trigger.value && r.active).length;
                const hasActiveRules = activeRulesCount > 0;
                
                return (
                  <Card key={trigger.value} className={`hover:shadow-md transition-shadow ${hasActiveRules ? 'border-green-300 bg-green-50' : ''}`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{trigger.label}</span>
                            {hasActiveRules && (
                              <Badge className="bg-green-600 text-white text-xs">
                                {activeRulesCount} {language === 'pt-BR' ? 'ativa(s)' : 'active'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {trigger.value === 'new_job' && (language === 'pt-BR' ? 'Dispara quando admin cria novo trabalho' : 'Triggers when admin creates new job')}
                            {trigger.value === 'job_updated' && (language === 'pt-BR' ? 'Dispara quando trabalho é atualizado' : 'Triggers when job is updated')}
                            {trigger.value === 'labor_bill_created' && (language === 'pt-BR' ? 'Dispara quando PM cria labor bill' : 'Triggers when PM creates labor bill')}
                            {trigger.value === 'labor_bill_paid' && (language === 'pt-BR' ? 'Dispara quando admin marca como pago' : 'Triggers when admin marks as paid')}
                            {trigger.value === 'job_reminder' && (language === 'pt-BR' ? 'Lembretes agendados (configurável)' : 'Scheduled reminders (configurable)')}
                            {trigger.value === 'report_saved' && (language === 'pt-BR' ? 'Dispara quando relatório é salvo' : 'Triggers when report is saved')}
                            {trigger.value === 'ticket_created' && (language === 'pt-BR' ? 'Dispara quando ticket é criado' : 'Triggers when ticket is created')}
                            {trigger.value === 'ticket_replied' && (language === 'pt-BR' ? 'Dispara quando admin responde ticket' : 'Triggers when admin replies to ticket')}
                            {trigger.value === 'announcement_published' && (language === 'pt-BR' ? 'Dispara quando anúncio é publicado' : 'Triggers when announcement is published')}
                            {trigger.value === 'truck_rental_requested' && (language === 'pt-BR' ? 'Dispara quando PM solicita aluguel' : 'Triggers when PM requests rental')}
                            {trigger.value === 'truck_rental_approved' && (language === 'pt-BR' ? 'Dispara quando aluguel é aprovado' : 'Triggers when rental is approved')}
                            {trigger.value === 'internal_notification_sent' && (language === 'pt-BR' ? 'Também envia push com notificação interna' : 'Also sends push with internal notification')}
                          </p>
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditingRule(null);
                            setRuleForm({ 
                              name: `${trigger.label} - Regra`,
                              trigger: trigger.value, 
                              template_key: trigger.value, 
                              recipients: [], 
                              active: true,
                              custom_title: '',
                              custom_body: '',
                              schedule_time: '18:00',
                              schedule_days_before: 1
                            });
                            setShowRuleModal(true);
                          }}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          {language === 'pt-BR' ? 'Configurar' : 'Configure'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
          
          {/* SEÇÃO 2: Regras Configuradas */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  📋 {language === 'pt-BR' ? 'Regras Configuradas' : 'Configured Rules'}
                </h3>
                <p className="text-sm text-gray-600">
                  {language === 'pt-BR' 
                    ? 'Todas as regras automáticas que você criou' 
                    : 'All automatic rules you have created'}
                </p>
              </div>
              <Button
                onClick={() => {
                  setEditingRule(null);
                  setRuleForm({ 
                    name: '', 
                    trigger: 'new_job', 
                    template_key: 'new_job', 
                    recipients: [], 
                    active: true,
                    custom_title: '',
                    custom_body: '',
                    schedule_time: '18:00',
                    schedule_days_before: 1
                  });
                  setShowRuleModal(true);
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                {language === 'pt-BR' ? 'Nova Regra' : 'New Rule'}
              </Button>
            </div>
          
          {rules.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center text-gray-500">
                <Settings className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{language === 'pt-BR' ? 'Nenhuma regra configurada' : 'No rules configured'}</p>
                <p className="text-xs mt-1">
                  {language === 'pt-BR' 
                    ? 'Crie regras para enviar notificações automaticamente' 
                    : 'Create rules to send notifications automatically'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <Card key={rule.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">{rule.name}</h3>
                          <Badge variant={rule.active ? 'default' : 'secondary'}>
                            {rule.active 
                              ? (language === 'pt-BR' ? 'Ativo' : 'Active')
                              : (language === 'pt-BR' ? 'Inativo' : 'Inactive')
                            }
                          </Badge>
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>
                            <span className="font-medium">{language === 'pt-BR' ? 'Quando:' : 'When:'}</span>{' '}
                            {triggerOptions.find(t => t.value === rule.trigger)?.label || rule.trigger}
                          </div>
                          <div>
                            <span className="font-medium">{language === 'pt-BR' ? 'Para:' : 'To:'}</span>{' '}
                            {rule.recipients.join(', ')}
                          </div>
                          <div>
                            <span className="font-medium">Template:</span> {rule.template_key}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleRule(rule)}
                        >
                          {rule.active ? '⏸️' : '▶️'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingRule(rule);
                            setRuleForm(rule);
                            setShowRuleModal(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          </div>
        </TabsContent>
        
        {/* ABA: Histórico */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{language === 'pt-BR' ? '📊 Últimas 50 Notificações' : '📊 Last 50 Notifications'}</span>
                <Button variant="outline" size="sm" onClick={loadLogs}>
                  {language === 'pt-BR' ? 'Atualizar' : 'Refresh'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{language === 'pt-BR' ? 'Nenhuma notificação enviada ainda' : 'No notifications sent yet'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-gray-900">
                            {log.title}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {log.body}
                          </div>
                          {log.image_url && (
                            <img
                              src={log.image_url}
                              alt=""
                              className="mt-2 w-24 h-16 object-cover rounded border"
                              data-testid={`push-log-image-${log.id}`}
                            />
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span>👤 {log.user_id}</span>
                            <span>📅 {formatNyDateTime(log.sent_at)}</span>
                            {log.template_key && <Badge className="text-xs">{log.template_key}</Badge>}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleResendPush(log)} disabled={loading}>
                            {language === 'pt-BR' ? 'Reenviar' : 'Resend'}
                          </Button>
                          {log.delivered ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              ✅ {language === 'pt-BR' ? 'Entregue' : 'Delivered'}
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 border-red-200">
                              ❌ {language === 'pt-BR' ? 'Falhou' : 'Failed'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {log.error && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                          {log.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal: Enviar Notificação */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              📤 {language === 'pt-BR' ? 'Enviar Notificação' : 'Send Notification'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Destinatários */}
            <div>
              <Label>{language === 'pt-BR' ? 'Destinatários' : 'Recipients'} *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {recipientOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={sendForm.recipients.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSendForm({ ...sendForm, recipients: [...sendForm.recipients, option.value] });
                        } else {
                          setSendForm({ 
                            ...sendForm, 
                            recipients: sendForm.recipients.filter(r => r !== option.value) 
                          });
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Template */}
            <div>
              <Label>{language === 'pt-BR' ? 'Template ou Customizado' : 'Template or Custom'}</Label>
              <select
                value={sendForm.template_key}
                onChange={(e) => setSendForm({ ...sendForm, template_key: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-2"
              >
                <option value="custom">{language === 'pt-BR' ? 'Mensagem Customizada' : 'Custom Message'}</option>
                {Object.keys(templates).map((key) => (
                  <option key={key} value={key}>{templates[key].title}</option>
                ))}
              </select>
              {sendForm.template_key !== 'custom' && (
                <p className="text-xs text-amber-600 mt-1 bg-amber-50 p-2 rounded">
                  💡 {language === 'pt-BR' 
                    ? 'Você pode personalizar o título e mensagem para este template' 
                    : 'You can customize the title and message for this template'}
                </p>
              )}
            </div>
            
            {/* Título e Corpo */}
            <div>
              <Label>{language === 'pt-BR' ? 'Título' : 'Title'} *</Label>
              <Input
                value={sendForm.title}
                onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                placeholder={language === 'pt-BR' ? 'Ex: Novo Trabalho Disponível' : 'Ex: New Job Available'}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label>{language === 'pt-BR' ? 'Mensagem' : 'Message'} *</Label>
              <Textarea
                value={sendForm.body}
                onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
                placeholder={language === 'pt-BR' 
                  ? 'Ex: Você tem um novo trabalho agendado para amanhã' 
                  : 'Ex: You have a new job scheduled for tomorrow'}
                className="mt-2"
                rows={3}
              />
            </div>

            {/* Imagem (opcional) */}
            <div>
              <Label>{language === 'pt-BR' ? '🖼️ Imagem (opcional)' : '🖼️ Image (optional)'}</Label>
              {sendForm.image_url ? (
                <div className="mt-2 flex items-start gap-3" data-testid="push-image-preview-wrap">
                  <img
                    src={sendForm.image_url}
                    alt="Preview"
                    className="w-32 h-24 object-cover rounded-lg border"
                    data-testid="push-image-preview"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                    onClick={() => setSendForm(prev => ({ ...prev, image_url: '' }))}
                    data-testid="push-image-remove-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {language === 'pt-BR' ? 'Remover' : 'Remove'}
                  </Button>
                </div>
              ) : (
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePushImageSelect}
                    disabled={imageUploading}
                    className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                    data-testid="push-image-input"
                  />
                  {imageUploading && (
                    <p className="text-xs text-indigo-600 mt-1">⏳ {language === 'pt-BR' ? 'Enviando imagem...' : 'Uploading image...'}</p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {language === 'pt-BR'
                  ? '📱 Android e Web exibem a imagem direto. iOS exibe após instalar a versão do app com a extensão de imagens (setup-push.sh).'
                  : '📱 Android and Web display the image directly. iOS requires the app version with the image extension (setup-push.sh).'}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendModal(false)}>
              {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSendNotification}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? '⏳' : '📤'} {language === 'pt-BR' ? 'Enviar' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal: Criar/Editar Regra */}

      {/* MODAL: Criar/Editar Template */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate 
                ? (language === 'pt-BR' ? '✏️ Editar Template' : '✏️ Edit Template')
                : (language === 'pt-BR' ? '➕ Novo Template' : '➕ New Template')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Título */}
            <div>
              <Label>{language === 'pt-BR' ? 'Título' : 'Title'} *</Label>
              <Input
                value={templateForm.title}
                onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                placeholder={language === 'pt-BR' ? 'Ex: 💼 Novo Labor Bill' : 'Ex: 💼 New Labor Bill'}
                className="mt-2"
              />
            </div>
            
            {/* Mensagem */}
            <div>
              <Label>{language === 'pt-BR' ? 'Mensagem' : 'Message'} *</Label>
              <Textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                placeholder={language === 'pt-BR' 
                  ? 'Ex: {customer_name} - #{order_number}' 
                  : 'Ex: {customer_name} - #{order_number}'}
                className="mt-2"
                rows={3}
              />
            </div>
            
            {/* Variáveis Disponíveis - Completo */}
            <details className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <summary className="cursor-pointer font-medium text-sm text-purple-900 flex items-center gap-2">
                📝 {language === 'pt-BR' ? 'Ver todas as variáveis disponíveis' : 'View all available variables'}
                <Badge className="bg-purple-600 text-white text-xs ml-auto">
                  30+ {language === 'pt-BR' ? 'variáveis' : 'variables'}
                </Badge>
              </summary>
              <div className="mt-3 max-h-96 overflow-y-auto pr-2 space-y-3">
                
                {/* Trabalhos & Calendário */}
                <div className="bg-white rounded p-3 border border-purple-100">
                  <div className="font-semibold text-xs text-purple-800 mb-2">🆕 TRABALHOS & CALENDÁRIO</div>
                  <div className="space-y-1 text-xs">
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{customer_name}'}</code> - Nome do cliente</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{pm_name}'}</code> - Nome do PM responsável</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{date}'}</code> - Data do trabalho (ex: 2026-01-30)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{time}'}</code> - Horário do trabalho (ex: 08:00)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{job_id}'}</code> - ID único do trabalho</div>
                  </div>
                </div>
                
                {/* Labor Bills */}
                <div className="bg-white rounded p-3 border border-purple-100">
                  <div className="font-semibold text-xs text-purple-800 mb-2">💼 LABOR BILLS</div>
                  <div className="space-y-1 text-xs">
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{order_number}'}</code> - Número da ordem (ex: 4565342)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{total}'}</code> - Valor total (ex: $500.00)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{bill_id}'}</code> - ID único do labor bill</div>
                  </div>
                </div>
                
                {/* Relatórios */}
                <div className="bg-white rounded p-3 border border-purple-100">
                  <div className="font-semibold text-xs text-purple-800 mb-2">📊 RELATÓRIOS</div>
                  <div className="space-y-1 text-xs">
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{report_type}'}</code> - Tipo do relatório (ex: Weekly Payroll)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{week_ending}'}</code> - Semana formato ISO (ex: 2026-01-25)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{week_ending_formatted}'}</code> - Semana formato US (ex: 01/25/2026)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{entries_count}'}</code> - Quantidade de trabalhos no relatório</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{saved_by}'}</code> - Quem salvou o relatório (ex: ADMIN)</div>
                  </div>
                </div>
                
                {/* Tickets / Suporte */}
                <div className="bg-white rounded p-3 border border-purple-100">
                  <div className="font-semibold text-xs text-purple-800 mb-2">🎫 TICKETS / SUPORTE</div>
                  <div className="space-y-1 text-xs">
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{ticket_title}'}</code> - Título do ticket</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{ticket_id}'}</code> - ID único do ticket</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{priority}'}</code> - Prioridade (low, medium, high, urgent)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{replied_by}'}</code> - Quem respondeu (geralmente ADMIN)</div>
                  </div>
                </div>
                
                {/* Anúncios */}
                <div className="bg-white rounded p-3 border border-purple-100">
                  <div className="font-semibold text-xs text-purple-800 mb-2">📢 ANÚNCIOS</div>
                  <div className="space-y-1 text-xs">
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{announcement_title}'}</code> - Título do anúncio</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{content}'}</code> - Conteúdo (primeiros 100 caracteres)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{priority}'}</code> - Prioridade do anúncio</div>
                  </div>
                </div>
                
                {/* Trucks / Aluguel */}
                <div className="bg-white rounded p-3 border border-purple-100">
                  <div className="font-semibold text-xs text-purple-800 mb-2">🚚 ALUGUEL DE TRUCKS</div>
                  <div className="space-y-1 text-xs">
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{truck_model}'}</code> - Modelo do caminhão (ex: Ford F-150)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{truck_plate}'}</code> - Placa do caminhão</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{date_start}'}</code> - Data início do aluguel</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{date_end}'}</code> - Data fim do aluguel</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{approved_by}'}</code> - Quem aprovou (geralmente ADMIN)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{pm_username}'}</code> - Username do PM (ex: PM1)</div>
                  </div>
                </div>
                
                {/* Notificações Internas */}
                <div className="bg-white rounded p-3 border border-purple-100">
                  <div className="font-semibold text-xs text-purple-800 mb-2">📨 NOTIFICAÇÕES INTERNAS</div>
                  <div className="space-y-1 text-xs">
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{notification_title}'}</code> - Título da notificação interna</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{notification_message}'}</code> - Mensagem da notificação</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{target_user}'}</code> - Para quem foi enviada (ALL ou PM específico)</div>
                    <div><code className="bg-purple-100 px-1.5 py-0.5 rounded font-mono text-purple-700">{'{sent_by}'}</code> - Quem enviou (geralmente ADMIN)</div>
                  </div>
                </div>
                
                {/* Dica final */}
                <div className="bg-purple-100 rounded p-2 text-xs text-purple-800">
                  💡 <strong>{language === 'pt-BR' ? 'Dica:' : 'Tip:'}</strong> {language === 'pt-BR' 
                    ? 'As variáveis são preenchidas automaticamente quando o evento acontece. Copie e cole no título ou mensagem acima.' 
                    : 'Variables are automatically filled when the event happens. Copy and paste in title or message above.'}
                </div>
              </div>
            </details>
            
            {/* Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="font-medium text-blue-900 mb-2 text-sm">📱 Preview</div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="font-semibold text-sm">{templateForm.title || '(título)'}</div>
                <div className="text-xs text-gray-600 mt-1">{templateForm.body || '(mensagem)'}</div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={async () => {
                if (!templateForm.title || !templateForm.body) {
                  toast.error(language === 'pt-BR' ? 'Preencha título e mensagem!' : 'Fill title and message!');
                  return;
                }
                
                try {
                  const token = sessionStorage.getItem('access_token');
                  
                  // Auto-generate key from title if creating new template
                  let key = templateForm.key;
                  if (!editingTemplate) {
                    // Generate key from title: "Relatório Pronto" -> "relatorio_pronto"
                    key = templateForm.title
                      .toLowerCase()
                      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
                      .replace(/[^a-z0-9\s]/g, '') // Remove special chars
                      .trim()
                      .replace(/\s+/g, '_') // Spaces to underscores
                      .substring(0, 50); // Max 50 chars
                  }
                  
                  const payload = {
                    key: key,
                    title: templateForm.title,
                    body: templateForm.body,
                    data: templateForm.data
                  };
                  
                  if (editingTemplate) {
                    await axios.put(`${API}/push/templates/${editingTemplate}`, payload, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success(language === 'pt-BR' ? 'Template atualizado!' : 'Template updated!');
                  } else {
                    await axios.post(`${API}/push/templates`, payload, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success(language === 'pt-BR' ? 'Template criado!' : 'Template created!');
                  }
                  
                  setShowTemplateModal(false);
                  loadTemplates();
                } catch (error) {
                  console.error('Erro:', error);
                  toast.error(error.response?.data?.detail || (language === 'pt-BR' ? 'Erro ao salvar' : 'Error saving'));
                }
              }}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? '⏳' : '✅'} {language === 'pt-BR' ? 'Salvar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showRuleModal} onOpenChange={setShowRuleModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingRule 
                ? (language === 'pt-BR' ? '✏️ Editar Regra' : '✏️ Edit Rule')
                : (language === 'pt-BR' ? '➕ Nova Regra' : '➕ New Rule')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Nome */}
            <div>
              <Label>{language === 'pt-BR' ? 'Nome da Regra' : 'Rule Name'} *</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder={language === 'pt-BR' ? 'Ex: Notificar PM ao criar trabalho' : 'Ex: Notify PM on new job'}
                className="mt-2"
              />
            </div>
            
            {/* Trigger */}
            <div>
              <Label>{language === 'pt-BR' ? 'Quando Enviar (Evento)' : 'When to Send (Event)'} *</Label>
              <select
                value={ruleForm.trigger}
                onChange={(e) => setRuleForm({ ...ruleForm, trigger: e.target.value, template_key: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-2"
              >
                {triggerOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            
            {/* Template Base */}
            <div>
              <Label>Template Base *</Label>
              <select
                value={ruleForm.template_key}
                onChange={(e) => setRuleForm({ ...ruleForm, template_key: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-2"
              >
                {Object.keys(templates).map((key) => (
                  <option key={key} value={key}>{templates[key].title}</option>
                ))}
              </select>
            </div>
            
            {/* Título Customizado */}
            <div>
              <Label>{language === 'pt-BR' ? 'Título Personalizado (opcional)' : 'Custom Title (optional)'}</Label>
              <Input
                value={ruleForm.custom_title || ''}
                onChange={(e) => setRuleForm({ ...ruleForm, custom_title: e.target.value })}
                placeholder={language === 'pt-BR' ? 'Deixe vazio para usar o título do template' : 'Leave empty to use template title'}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                {language === 'pt-BR' 
                  ? '💡 Use variáveis: {customer_name}, {date}, {time}, {pm_name}' 
                  : '💡 Use variables: {customer_name}, {date}, {time}, {pm_name}'}
              </p>
            </div>
            
            {/* Mensagem Customizada */}
            <div>
              <Label>{language === 'pt-BR' ? 'Mensagem Personalizada (opcional)' : 'Custom Message (optional)'}</Label>
              <Textarea
                value={ruleForm.custom_body || ''}
                onChange={(e) => setRuleForm({ ...ruleForm, custom_body: e.target.value })}
                placeholder={language === 'pt-BR' ? 'Deixe vazio para usar a mensagem do template' : 'Leave empty to use template message'}
                className="mt-2"
                rows={3}
              />
            </div>
            
            {/* Configurações de Horário (para lembretes) */}
            {ruleForm.trigger === 'job_reminder' && (
              <div className="border border-amber-200 bg-amber-50 p-4 rounded-lg space-y-3">
                <h4 className="font-medium text-amber-900">
                  ⏰ {language === 'pt-BR' ? 'Configurações de Lembrete' : 'Reminder Settings'}
                </h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{language === 'pt-BR' ? 'Dias antes' : 'Days before'}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="7"
                      value={ruleForm.schedule_days_before || 1}
                      onChange={(e) => setRuleForm({ ...ruleForm, schedule_days_before: parseInt(e.target.value) || 1 })}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>{language === 'pt-BR' ? 'Horário (HH:MM)' : 'Time (HH:MM)'}</Label>
                    <Input
                      type="time"
                      value={ruleForm.schedule_time || '18:00'}
                      onChange={(e) => setRuleForm({ ...ruleForm, schedule_time: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-amber-700">
                  {language === 'pt-BR' 
                    ? `Enviará o lembrete ${ruleForm.schedule_days_before || 1} dia(s) antes do trabalho às ${ruleForm.schedule_time || '18:00'}` 
                    : `Will send reminder ${ruleForm.schedule_days_before || 1} day(s) before the job at ${ruleForm.schedule_time || '18:00'}`}
                </p>
              </div>
            )}
            
            {/* Destinatários Globais */}
            <div>
              <Label>{language === 'pt-BR' ? 'Destinatários' : 'Recipients'} *</Label>
              <p className="text-xs text-gray-600 mt-1 mb-2">
                {language === 'pt-BR' 
                  ? 'Selecione quem deve receber esta notificação' 
                  : 'Select who should receive this notification'}
              </p>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                {recipientOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 p-2 border border-gray-300 bg-white rounded cursor-pointer hover:bg-blue-50 hover:border-blue-400 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={ruleForm.recipients.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRuleForm({ ...ruleForm, recipients: [...ruleForm.recipients, option.value] });
                        } else {
                          setRuleForm({ 
                            ...ruleForm, 
                            recipients: ruleForm.recipients.filter(r => r !== option.value) 
                          });
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Ativo/Inativo */}
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
              <input
                type="checkbox"
                id="rule-active"
                checked={ruleForm.active}
                onChange={(e) => setRuleForm({ ...ruleForm, active: e.target.checked })}
                className="w-5 h-5"
              />
              <label htmlFor="rule-active" className="text-sm font-medium cursor-pointer">
                {ruleForm.active 
                  ? (language === 'pt-BR' ? '✅ Regra Ativa (notificações serão enviadas)' : '✅ Rule Active (notifications will be sent)')
                  : (language === 'pt-BR' ? '⏸️ Regra Inativa (notificações NÃO serão enviadas)' : '⏸️ Rule Inactive (notifications will NOT be sent)')}
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleModal(false)}>
              {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSaveRule}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? '⏳' : '✅'} {language === 'pt-BR' ? 'Salvar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PushNotificationsPanel;
